import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Space, Typography, message } from 'antd';
import { CheckOutlined, ClockCircleOutlined, PhoneOutlined, RedoOutlined } from '@ant-design/icons';
import { changeCallbackStatus } from '@/services/app/tenant/call-queue';
import { QueueItem } from '@/contexts/CallbackQueueContext';
import { onPostCallPrompt } from './postCallPrompt';

/**
 * "How did the callback go?" — shown to the CSR who placed a callback-queue call once
 * their leg ends and the row is still in_progress (i.e. the call connected). Mounted once
 * in app.tsx next to the site-wide banners; fed by watchCallbackCall in postCallPrompt.ts.
 *
 * Resolved closes the callback. Call Again records a no-answer attempt (voicemail, wrong
 * person, "call me later") and puts it back in the pending queue. Decide later leaves it
 * In Progress on the Call Board — and, per the client, requires a reason, which is added
 * to the callback's notes so whoever picks it up on the Call Board knows why it is open.
 *
 * The modal cannot be dismissed any other way (no close icon, mask or Esc): every
 * connected callback ends with one of the three answers.
 */
const CallbackPostCallPrompt: React.FC = () => {
  const [item, setItem] = useState<QueueItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [laterMode, setLaterMode] = useState(false);
  const [laterReason, setLaterReason] = useState('');

  useEffect(() => onPostCallPrompt((i) => {
    setItem(i);
    setLaterMode(false);
    setLaterReason('');
  }), []);

  const close = () => {
    setItem(null);
    setLaterMode(false);
    setLaterReason('');
  };

  const act = async (status: 'resolved' | 'no_answer') => {
    if (!item) return;
    setBusy(true);
    try {
      const { changed } = await changeCallbackStatus(
        item,
        status,
        '',
        status === 'no_answer' ? 'voicemail or not reached' : undefined,
      );
      if (!changed) {
        message.info('This callback was already updated');
      } else if (status === 'resolved') {
        message.success('Callback resolved');
      } else {
        message.success('Back in the queue for another try');
      }
      close();
    } catch {
      message.error('Failed to update callback');
    } finally {
      setBusy(false);
    }
  };

  // Decide later: status stays in_progress; the reason is appended to the row's notes.
  const decideLater = async () => {
    if (!item) return;
    const reason = laterReason.trim();
    if (!reason) return;
    setBusy(true);
    try {
      const stamp = new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      await changeCallbackStatus(item, 'in_progress', '', `Decide later: ${reason} · ${stamp}`, { appendNotes: true });
      message.success('Kept In Progress — reason saved to the callback');
      close();
    } catch {
      message.error('Failed to save the reason');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={!!item}
      title="How did the callback go?"
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      zIndex={1100}
      destroyOnClose
    >
      {item && (
        <>
          <div style={{ marginBottom: 12 }}>
            <Typography.Text strong>
              <PhoneOutlined style={{ marginRight: 6 }} />
              {item.customer_name ? `${item.customer_name} · ` : ''}
              {item.customer_phone}
            </Typography.Text>
            {item.reason && (
              <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>{item.reason}</div>
            )}
          </div>

          {!laterMode ? (
            <>
              <Space wrap>
                <Button type="primary" icon={<CheckOutlined />} loading={busy} onClick={() => act('resolved')}>
                  Resolved
                </Button>
                <Button icon={<RedoOutlined />} loading={busy} onClick={() => act('no_answer')}>
                  Call again (voicemail / not reached)
                </Button>
                <Button type="text" icon={<ClockCircleOutlined />} disabled={busy} onClick={() => setLaterMode(true)}>
                  Decide later
                </Button>
              </Space>

              <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 12 }}>
                Call again puts it back in the queue with the attempt recorded. Decide later keeps it
                In Progress on the Call Board and asks you for a reason.
              </Typography.Text>
            </>
          ) : (
            <>
              <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
                Why is this callback being left open?
              </Typography.Text>
              <Input.TextArea
                autoFocus
                rows={3}
                maxLength={500}
                showCount
                value={laterReason}
                onChange={(e) => setLaterReason(e.target.value)}
                placeholder="e.g. customer asked for a quote first, waiting on the tech's answer…"
                disabled={busy}
              />
              <Space style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  icon={<ClockCircleOutlined />}
                  loading={busy}
                  disabled={!laterReason.trim()}
                  onClick={decideLater}
                >
                  Keep In Progress
                </Button>
                <Button disabled={busy} onClick={() => { setLaterMode(false); setLaterReason(''); }}>
                  Back
                </Button>
              </Space>
              <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 12 }}>
                The reason is added to the callback's notes on the Call Board, where it stays In
                Progress until someone marks it Resolved or No Answer.
              </Typography.Text>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default CallbackPostCallPrompt;
